import { roundness } from "@provider/dripsy";
import type { Account, AccountUpdateInput } from "@type/account";
import type { DripsyFinalTheme } from "dripsy";
import { useDripsyTheme, View } from "dripsy";
import * as React from "react";
import type { ScrollView } from "react-native";
import { AppState, Linking, Platform, useWindowDimensions } from "react-native";

import { allowedZipcodes } from "./data";
import useCustomWindowDimensions from "./hooks/use-custom-window-dimensions";
import type { PaginationResponse } from "@type/pagination";

export function chunk<T>(arr: T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(arr.length / size) }, (v, i) => arr.slice(i * size, i * size + size));
}
export const moneyFormatter = new Intl.NumberFormat("nl-NL", {
  style: "currency",
  currency: "EUR",
  currencySign: "standard",
});

export const formatMoneyWithoutEuro = new Intl.NumberFormat("nl-NL", {
  style: "decimal",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format;

export const notSelectableOnWebStyle: any = Platform.OS === "web" ? { userSelect: "none" } : {};

export function useLatest<T>(value: T) {
  const ref = React.useRef(value);
  ref.current = value;
  return ref;
}
export const groupByToMap = <T, Q>(array: T[], predicate: (value: T, index: number, array: T[]) => Q) =>
  array.reduce(
    (acc, value, index, array) => {
      // @ts-ignore
      (acc[predicate(value, index, array)] ||= []).push(value);
      return acc;
    },
    {} as { [key: string]: T[] },
  );

export type DripsyColor = keyof DripsyFinalTheme["colors"]; //(string & {}) |

export function useDripsyColor({ color }: { color: DripsyColor }) {
  const { colors } = useDripsyTheme().theme;
  if (colors?.[color]) {
    color = colors[color] as DripsyColor;
  }
  return color;
}

export function splitPrice(price: number) {
  const priceString = moneyFormatter.format(price);
  const priceSplit = priceString.split(",");
  const amountWithCurrency = priceSplit[0]!;
  const amount = amountWithCurrency.replace("€\xa0", "");
  const cents = priceSplit[1]!;
  return { amount, cents };
}

export function getUpdateProfileData({
  contentType,
  mailType,
  profile,
}: {
  contentType: AccountUpdateInput["content_type"];
  mailType?: AccountUpdateInput["send_to_mail_type"];
  profile: Account;
}): AccountUpdateInput {
  const obj: AccountUpdateInput = {
    id: profile.id,
    content_type: contentType,
    country: profile.country,
    country_invoice: profile.country_invoice,
    first_name: profile.first_name,
    last_name: profile.last_name,
    number: profile.number,
    number_invoice: profile.number_invoice,
    phone: profile.phone,
    email: profile.email,
    place: profile.place,
    place_invoice: profile.place_invoice,
    street: profile.street,
    street_invoice: profile.street_invoice,
    zipcode: profile.zipcode,
    zipcode_invoice: profile.zipcode_invoice,
    type_delivery: profile.type_delivery,
    metadata: profile.metadata,
    key_available: profile.key_available,
    receive_newsletter: profile.receive_newsletter,
    remember_me_to_order: profile.remember_me_to_order,
  };

  if (mailType) {
    obj.send_to_mail_type = mailType;
  }

  return obj;
}

export const paymentTypes: string[] = ["Automatische incasso (NL)", "Domiciliëring (BE)"]; //, "iDeal", "Bankcontact"];

export function paymentTypeToLabel(paymentType: number): string {
  return paymentTypes[paymentType - 1] || "Niet bekend";
}

export function doesDeliverToZipcode(zipcode: string, area: { nl: string[]; be: string[] } = allowedZipcodes) {
  let locale: "nl" | "be" = "nl";
  let length = 4;

  if (zipcode.match(/[a-z]/i)) {
    locale = "nl";
    length = 6;
  } else {
    locale = "be";
    length = 4;
  }

  if (area[locale].includes(zipcode.substring(0, 4)) && zipcode.length >= length) {
    return true;
  } else {
    return false;
  }
}

// export function hexToRGB(hex: string, alpha?: string) {
//   const r = parseInt(hex.slice(1, 3), 16);
//   const g = parseInt(hex.slice(3, 5), 16);
//   const b = parseInt(hex.slice(5, 7), 16);
//
//   if (alpha) {
//     return `rgba(${r}, ${g}, ${b}, ${alpha || 1})`;
//   }
//
//   return `rgb(${r}, ${g}, ${b})`;
// }
const timerMap = new Map();
export function href(url: string) {
  return Platform.OS === "web" ? { href: url, target: "_blank" } : { onPress: () => Linking.openURL(url) };
}
export function debounce<T extends Function>(fn: T, key: string, delay = 300) {
  if (!key) throw Error("You need to set a key");
  ((...args) => {
    let timeOutId = timerMap.get(key);
    if (timeOutId) {
      clearTimeout(timeOutId);
    }
    timeOutId = setTimeout(() => {
      fn(...args);
      timerMap.delete(key);
    }, delay);
    timerMap.set(key, timeOutId);
  })();
}

export function normalDebounce<F extends (...params: any[]) => void>(fn: F, delay: number) {
  let timeoutID: number | null = null;
  return function (this: any, ...args: any[]) {
    if (timeoutID) {
      clearTimeout(timeoutID);
    }
    timeoutID = window.setTimeout(() => fn.apply(this, args), delay);
  } as F;
}

const shortDateFormatter = new Intl.DateTimeFormat("nl", { dateStyle: "long" });
export function formatDate(dateString: string) {
  dateString = dateString.replace(" ", "T") + "Z";
  const date = new Date(dateString);
  return shortDateFormatter.format(date);
}

export function dateFromUnix(n: number): Date {
  return new Date(n * 1000);
}

export function useOrientation() {
  const { width, height } = useWindowDimensions();
  return height >= width ? "PORTRAIT" : "LANDSCAPE";
}

export function looksLikeTelephone(str: string): boolean {
  if (str.length !== 10) {
    return false;
  }
  const isNum = /^\d+$/.test(str);
  if (!isNum) {
    return false;
  }
  return true;
}

/**
 * Exactly like `useEffect`, except that the effect only executes on subsequent
 * updates after a component mounts.
 */
export function useEffectAfterMount(effect: () => void | (() => void), deps: React.DependencyList) {
  const isInitialRender = React.useRef(true);

  React.useEffect(() => {
    isInitialRender.current = false;
    return () => {
      isInitialRender.current = true;
    };
  }, []);

  React.useEffect(() => {
    if (!isInitialRender.current) {
      return effect();
    }
  }, deps);
}

// you can add your own validate functions
export function looksLikeMail(str: string): boolean {
  const lastAtPos = str.lastIndexOf("@");
  const lastDotPos = str.lastIndexOf(".");
  return (
    lastAtPos < lastDotPos && lastAtPos > 0 && str.indexOf("@@") === -1 && lastDotPos > 2 && str.length - lastDotPos > 2
  );
}

export function ShimmerView({ width, height }: { width: number | string; height: number | string }) {
  return (
    <View
      sx={{
        backgroundColor: "$grey",
        width,
        height,
        borderRadius: roundness / 2,
        opacity: 0.7,
      }}
    />
  );
}

export const useAnchor = ({
  width,
  anchor,
  scrollViewRef,
}: {
  width: number;
  anchor: string | undefined;
  scrollViewRef: React.MutableRefObject<ScrollView> | undefined;
}) => {
  React.useEffect(() => {
    if (Platform.OS === "web" && width > 1100 && anchor !== undefined) {
      let anchorOffset = 0;
      const h2 = [...((document.querySelectorAll("h2") as any) || ([] as any))];
      const h1 = [...((document.querySelectorAll("h1") as any) || ([] as any))];

      const primary: any = [...h1, ...h2].find((el) => el.innerText === anchor && el.offsetTop > 10);
      const fallback: any = [...h1, ...h2].find(
        (el) => el.innerText.toLowerCase().startsWith(anchor.toLowerCase()) && el.offsetTop > 10,
      );

      const element = primary || fallback;

      if (element) {
        anchorOffset = element.offsetTop;
        scrollViewRef.current.scrollTo({ y: anchorOffset - 40, animated: false });
      }
    }
  }, [width, anchor]);
};

export const postCodeInputHelper = {
  minLength: 4,
  maxLength: 6,
  enhance: (v: string | undefined) => {
    if (!v) {
      return "";
    }
    v = v.replace(" ", "");
    if (v.length > 6) {
      v = v.substring(0, 6);
    }
    v = v.replace(/[^a-zA-Z\d:]/g, "");
    v = v.toUpperCase();
    return v;
  },

  validate: (v: string | undefined) => {
    if (!v) {
      return true;
    }
    if (!/^[0-9]+$/.test(v.substring(0, 4))) {
      return "Ongeldige postcode";
    }
    if (v.length > 4) {
      if (!/^[A-Za-z]+$/.test(v.substring(4, 6))) {
        return "Ongeldige postcode";
      }
    }

    return true;
  },
};

export function capitalizeFirstLetter(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
export function useIsSize() {
  const { width } = useCustomWindowDimensions();

  return {
    isBigScreen: width > 1200,
    isSm: width < 1000,
    isSmallerThan800: width < 800,
    isBig: width > 800,
    isSx: width < 600,
    isSxx: width < 300,
  };
}
export function flattenPages<T>(data: undefined | { pages: PaginationResponse<T>[]; pageParams?: unknown[] }): T[] {
  return data?.pages?.map((page) => page.data)?.flat() || [];
}

export function useAppState() {
  const [appState, setAppState] = React.useState(AppState.currentState);

  React.useEffect(() => {
    const listener = AppState.addEventListener("change", setAppState);
    return () => listener.remove();
  }, []);
  return appState;
}
